import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, ChevronUp, Users, AlertTriangle, Target, TrendingUp, Clock, Star, Sparkles, Send, Search, Filter, X, Settings as SettingsIcon, BookOpenIcon, CodeIcon, LayoutIcon, GlobeIcon, FileCodeIcon, ArrowRight, MessageSquare, MoreVertical, ExternalLink, ChevronsLeftRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// ─── Types ───────────────────────────────────────────────────────────────────
type Priority = 'critical' | 'warning' | 'positive';
interface SkillInsight {
  id: number;
  skill: string;
  headline: string;
  priority: Priority;
  affectedLearners: number;
  metrics: {
    label: string;
    value: string;
  }[];
  struggleAreas?: string[];
  suggestedActions: string[];
  timestamp: string;
  learnerName?: string;
}
interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
}
// ─── Data ────────────────────────────────────────────────────────────────────
const mockLearners = [{
  id: 1,
  name: 'Linda Taylor',
  email: 'linda.taylor@example.com'
}, {
  id: 2,
  name: 'John Smith',
  email: 'john.smith@example.com'
}, {
  id: 3,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com'
}, {
  id: 4,
  name: 'Michael Chen',
  email: 'mchen@example.com'
}, {
  id: 5,
  name: 'Emily Davis',
  email: 'emily.d@example.com'
}, {
  id: 6,
  name: 'Alex Rivera',
  email: 'alex.r@example.com'
}, {
  id: 7,
  name: 'Lisa Wong',
  email: 'lisa.w@example.com'
}];
const skillInsights: SkillInsight[] = [{
  id: 1,
  skill: 'CSS Fundamentals',
  headline: '68% first-attempt failure rate — well below acceptable threshold',
  priority: 'critical',
  affectedLearners: 42,
  metrics: [{
    label: 'Failing',
    value: '42 of 62 learners'
  }, {
    label: 'Avg score',
    value: '54%'
  }, {
    label: 'Pass threshold',
    value: '70%'
  }],
  struggleAreas: ['Questions 4–7 most missed', 'High confidence, low accuracy', 'Avg 12 min — rushing through'],
  suggestedActions: ['Schedule group study session', 'Assign peer mentors', 'Send encouragement message', 'Extend practice time'],
  timestamp: 'Detected 2 hours ago'
}, {
  id: 2,
  skill: 'JavaScript Basics',
  headline: '34% misinformation rate on async/await concepts — 2.8x above average',
  priority: 'critical',
  affectedLearners: 89,
  metrics: [{
    label: 'Learners affected',
    value: '89'
  }, {
    label: 'Misinfo rate',
    value: '34% vs 12% avg'
  }, {
    label: 'Attempts analyzed',
    value: '156'
  }],
  struggleAreas: ['Question 6 — most failed', 'Confident but wrong on 34%', '67% abandon mid-question'],
  suggestedActions: ['Host Q&A session', 'Share additional resources', 'Create study groups', 'Offer office hours'],
  timestamp: 'Detected 3 hours ago'
}, {
  id: 3,
  skill: 'Responsive Design',
  headline: 'Only 12% enrollment despite being a core requirement',
  priority: 'warning',
  affectedLearners: 218,
  metrics: [{
    label: 'Enrolled',
    value: '30 of 248 learners'
  }, {
    label: 'Available',
    value: '45 days'
  }, {
    label: 'Feedback',
    value: '"intimidating title"'
  }],
  suggestedActions: ['Send enrollment reminder', 'Highlight skill benefits', 'Share success stories', 'Offer enrollment support'],
  timestamp: 'Detected 1 day ago'
}, {
  id: 4,
  skill: 'All Skills',
  headline: 'Weekend attempts show 41% higher failure rate than weekday',
  priority: 'warning',
  affectedLearners: 187,
  metrics: [{
    label: 'Attempts analyzed',
    value: '892'
  }, {
    label: 'Weekend fail rate',
    value: '58%'
  }, {
    label: 'Weekday fail rate',
    value: '41%'
  }],
  suggestedActions: ['Add weekend support hours', 'Send preparation reminders', 'Create weekend study groups', 'Share time management tips'],
  timestamp: 'Detected 5 hours ago'
}, {
  id: 5,
  skill: 'HTML Basics',
  headline: 'Completion rate increased 23% after adding video tutorials',
  priority: 'positive',
  affectedLearners: 124,
  metrics: [{
    label: 'Pass rate',
    value: '64% → 87%'
  }, {
    label: 'Avg attempts',
    value: '4.2 → 3.1'
  }, {
    label: 'Learners benefited',
    value: '124'
  }],
  suggestedActions: ['Celebrate with learners', 'Share success metrics', 'Recognize top performers', 'Gather learner feedback'],
  timestamp: 'Detected 1 day ago'
}, {
  id: 6,
  skill: 'JavaScript Basics',
  headline: 'Multiple learners showing declining scores across retakes',
  priority: 'critical',
  affectedLearners: 8,
  metrics: [{
    label: 'Learners with 3+ fails',
    value: '8'
  }, {
    label: 'Score trend',
    value: 'Declining per attempt'
  }, {
    label: 'Dropout risk',
    value: 'High'
  }],
  suggestedActions: ['Schedule 1-on-1 check-ins', 'Assess learning barriers', 'Connect with peer mentors', 'Provide encouragement'],
  timestamp: 'Detected 4 hours ago'
},
// ─── Individual Learner Insights ───
{
  id: 101,
  skill: 'JavaScript Basics',
  headline: 'Failed JavaScript Basics 3 times with declining scores (68% → 65% → 58%)',
  priority: 'critical',
  affectedLearners: 1,
  learnerName: 'John Smith',
  metrics: [{
    label: 'Attempts',
    value: '3'
  }, {
    label: 'Latest score',
    value: '58%'
  }, {
    label: 'Misinfo rate',
    value: '45%'
  }],
  struggleAreas: ['Questions 8–10 repeatedly missed', 'Same wrong answer each retake', 'Marks "sure" but scoring low'],
  suggestedActions: ['Schedule urgent 1-on-1', 'Assess support needs', 'Assign peer mentor'],
  timestamp: 'Detected 2 days ago'
}, {
  id: 102,
  skill: 'All Skills',
  headline: 'Achieved 95%+ on all 4 assessments on first attempt',
  priority: 'positive',
  affectedLearners: 1,
  learnerName: 'Sarah Johnson',
  metrics: [{
    label: 'Assessments',
    value: '4 of 4 passed'
  }, {
    label: 'Avg score',
    value: '96%'
  }, {
    label: 'Ahead of schedule',
    value: '2 weeks'
  }],
  suggestedActions: ['Send congratulations', 'Invite to mentor program', 'Share success story'],
  timestamp: 'Detected 1 day ago'
}, {
  id: 103,
  skill: 'CSS Fundamentals',
  headline: 'No activity for 7 days — last score was 62%',
  priority: 'warning',
  affectedLearners: 1,
  learnerName: 'Michael Chen',
  metrics: [{
    label: 'Days inactive',
    value: '7'
  }, {
    label: 'Last score',
    value: '62%'
  }, {
    label: 'Struggle area',
    value: 'CSS Layouts'
  }],
  suggestedActions: ['Send check-in message', 'Offer support call', 'Share helpful resources'],
  timestamp: 'Detected 7 days ago'
}, {
  id: 104,
  skill: 'HTML Basics',
  headline: 'Improved from 65% to 88% after focused practice',
  priority: 'positive',
  affectedLearners: 1,
  learnerName: 'Emily Davis',
  metrics: [{
    label: 'Score improvement',
    value: '65% → 88%'
  }, {
    label: 'Mastery gain',
    value: '60% → 92%'
  }, {
    label: 'Attempts',
    value: '2'
  }],
  suggestedActions: ['Send encouragement', 'Celebrate progress', 'Share as success story'],
  timestamp: 'Detected 1 day ago'
}, {
  id: 105,
  skill: 'Multiple Skills',
  headline: 'Consistently scoring below 50% across all modules',
  priority: 'critical',
  affectedLearners: 1,
  learnerName: 'Alex Rivera',
  metrics: [{
    label: 'Assessments',
    value: '5 attempted'
  }, {
    label: 'Avg score',
    value: '45%'
  }, {
    label: 'Misinfo rate',
    value: 'High'
  }],
  struggleAreas: ['Missing questions across all modules', 'Answer pattern suggests guessing', 'Quits before finishing'],
  suggestedActions: ['Schedule urgent meeting', 'Assess learning needs', 'Create support plan'],
  timestamp: 'Detected 3 days ago'
}, {
  id: 106,
  skill: 'Responsive Design',
  headline: 'Taking unusually long between attempts (10+ days gaps)',
  priority: 'warning',
  affectedLearners: 1,
  learnerName: 'Lisa Wong',
  metrics: [{
    label: 'Avg score',
    value: '75%'
  }, {
    label: 'Avg gap',
    value: '10+ days'
  }, {
    label: 'Pattern',
    value: 'Time management'
  }],
  suggestedActions: ['Check on progress', 'Offer scheduling support', 'Discuss barriers'],
  timestamp: 'Detected 5 days ago'
}, {
  id: 107,
  skill: 'CSS Fundamentals',
  headline: 'Struggling with Flexbox — 3 failed attempts on layout questions',
  priority: 'critical',
  affectedLearners: 1,
  learnerName: 'John Smith',
  metrics: [{
    label: 'Failed questions',
    value: '8 of 12'
  }, {
    label: 'Flexbox score',
    value: '22%'
  }, {
    label: 'Time spent',
    value: '8 min (low)'
  }],
  struggleAreas: ['Question 5 — 3 failed attempts', 'Missed 8 of 12 questions', 'High confidence, 22% correct'],
  suggestedActions: ['Share Flexbox resources', 'Recommend practice tools', 'Connect with CSS mentor'],
  timestamp: 'Detected 3 days ago'
},
// ─── Kudos: System-Wide Skill Success ───
{
  id: 108,
  skill: 'HTML Basics',
  headline: '92% of learners scored above 85% on HTML Basics this month — highest skill-wide performance ever',
  priority: 'positive',
  affectedLearners: 114,
  metrics: [{
    label: 'Learners above 85%',
    value: '114 of 124'
  }, {
    label: 'Avg score',
    value: '91%'
  }, {
    label: 'First-attempt pass rate',
    value: '87%'
  }],
  suggestedActions: ['Send team celebration', 'Recognize top performers', 'Share success metrics', 'Gather learner feedback'],
  timestamp: 'Detected 6 hours ago'
},
// ─── Kudos: Individual Learner Success ───
{
  id: 109,
  skill: 'CSS Fundamentals',
  headline: 'Achieved mastery on CSS Fundamentals after dedicated practice — score jumped from 58% to 94%',
  priority: 'positive',
  affectedLearners: 1,
  learnerName: 'Linda Taylor',
  metrics: [{
    label: 'Score improvement',
    value: '58% → 94%'
  }, {
    label: 'Mastery rate',
    value: '96%'
  }, {
    label: 'Attempts to pass',
    value: '3 (improved each time)'
  }],
  suggestedActions: ['Send congratulations', 'Feature as success story', 'Invite to mentor program', 'Celebrate achievement'],
  timestamp: 'Detected 12 hours ago'
}];
const suggestedQuestions = ['Which skills need the most attention right now?', 'Why are learners struggling with CSS Fundamentals?', "What's the completion trend for JavaScript Basics?", 'How can we improve the Responsive Design enrollment?', 'What patterns do you see in assessment failures?', 'Compare skill performance this month vs last month'];
const mockResponses: Record<string, string> = {
  'Which skills need the most attention right now?': "Based on current data, I'd prioritize these skills:\n\n**1. CSS Fundamentals** — Critical\n68% of learners are failing on first attempt. The biggest struggle areas are Flexbox (82% error rate) and Grid Layout (76%). I recommend reviewing the content difficulty and adding interactive exercises.\n\n**2. JavaScript Basics** — Critical\nAsync/await concepts show a 34% misinformation rate, which is 2.8x higher than the skill average. This suggests the content may be introducing misconceptions. Additionally, 8 learners show declining scores across retakes.\n\n**3. Responsive Design** — Warning\nOnly 12% enrollment despite being required. Learner feedback suggests the title is intimidating.",
  'Why are learners struggling with CSS Fundamentals?': "Looking at the data for CSS Fundamentals:\n\n**Key findings:**\n• Flexbox has an 82% error rate — learners consistently confuse flex-direction and justify-content\n• Grid Layout shows 76% errors, particularly on grid-template-areas\n• Positioning concepts (71% error) — absolute vs relative positioning is the main confusion point\n\n**Contributing factors:**\n• First-attempt pass rate is only 32%, suggesting content may be too advanced for the prerequisites\n• Average time spent on the module is 12 minutes, well below the recommended 25 minutes\n• 78% of failures happen on questions 4-7, which cover advanced layout patterns\n\n**Recommendation:** Consider breaking CSS Fundamentals into two modules — 'CSS Layout Basics' and 'Advanced CSS Layouts' — to reduce cognitive load.",
  "What's the completion trend for JavaScript Basics?": "Here's the completion trend for JavaScript Basics over the last 8 weeks:\n\n**Completion Rate Trend:**\n• Week 1-2: 45% → 48% (slight improvement)\n• Week 3-4: 48% → 42% (decline after async content added)\n• Week 5-6: 42% → 38% (continued decline)\n• Week 7-8: 38% → 41% (slight recovery)\n\n**Key Observations:**\n• The decline correlates with the introduction of async/await content in Week 3\n• Learners who complete the optional practice exercises have a 73% pass rate vs 31% for those who skip them\n• Average attempts to pass: 3.2 (up from 2.1 before async content)\n\n**Recommendation:** The async/await section needs restructuring. Consider adding prerequisite checks and more guided examples before the assessment.",
  'How can we improve the Responsive Design enrollment?': "Based on learner feedback and enrollment data, here are actionable recommendations:\n\n**Why enrollment is low (12%):**\n• 64% of surveyed learners said the title sounds \"too advanced\"\n• 42% didn't know it was a core requirement\n• 28% cited unclear prerequisites\n\n**Recommended actions:**\n1. **Rename the skill** — Consider 'Building for All Screens' or 'Mobile-Friendly Web Design'\n2. **Add a preview module** — A 5-minute intro showing what they'll learn and why it matters\n3. **Send targeted nudges** — Learners who completed CSS Fundamentals are ideal candidates\n4. **Show the requirement** — Make it visible in the learning path that this is mandatory\n\n**Expected impact:** Similar renaming strategies have shown 35-50% enrollment increases in comparable platforms.",
  'What patterns do you see in assessment failures?': "I've identified several patterns across all skill assessments:\n\n**Timing Patterns:**\n• Weekend attempts have a 41% higher failure rate (58% vs 41% weekday)\n• Attempts after 9 PM show 28% lower scores on average\n• Monday morning attempts perform best (avg score: 78%)\n\n**Content Patterns:**\n• Questions requiring application (not recall) have 3x higher failure rates\n• Multi-step problems see 67% abandonment before completion\n• Visual/diagram questions perform better than text-only (12% higher scores)\n\n**Learner Patterns:**\n• Learners who space attempts 3+ days apart score 22% higher than same-day retakers\n• Those who review feedback before retaking improve by 15% on average\n• Group study participants have 34% higher first-attempt pass rates\n\n**Top Recommendation:** Implement a mandatory 48-hour cooldown between attempts with guided review materials.",
  'Compare skill performance this month vs last month': "Here's the month-over-month comparison:\n\n**HTML Basics** ✅\n• Pass rate: 64% → 87% (+23%)\n• Avg attempts: 4.2 → 3.1 (-26%)\n• Driver: Video tutorial additions\n\n**CSS Fundamentals** 🔴\n• Pass rate: 38% → 32% (-6%)\n• Avg attempts: 3.8 → 4.5 (+18%)\n• Driver: Increased difficulty in Flexbox section\n\n**JavaScript Basics** 🔴\n• Pass rate: 48% → 41% (-7%)\n• Misinfo rate: 22% → 34% (+55%)\n• Driver: New async/await content causing confusion\n\n**Responsive Design** 🟡\n• Enrollment: 8% → 12% (+50% relative)\n• Pass rate: 71% → 73% (+2%)\n• Driver: Minor improvements, still low enrollment\n\n**Overall:** 2 skills declining, 1 improving significantly, 1 stable. The HTML Basics video strategy should be replicated across struggling skills."
};
// ─── Subcomponents ───────────────────────────────────────────────────────────
function TypingIndicator() {
  return <div className="flex items-center gap-1.5 px-4 py-3" data-id="element-1304">
      <motion.div className="w-2 h-2 rounded-full bg-[#30718D]" animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 0.6,
      repeat: Infinity,
      delay: 0
    }} data-id="element-1305" />
      <motion.div className="w-2 h-2 rounded-full bg-[#30718D]" animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 0.6,
      repeat: Infinity,
      delay: 0.15
    }} data-id="element-1306" />
      <motion.div className="w-2 h-2 rounded-full bg-[#30718D]" animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 0.6,
      repeat: Infinity,
      delay: 0.3
    }} data-id="element-1307" />
    </div>;
}
function SkillIcon({
  skill
}: {
  skill: string;
}) {
  const className = 'w-5 h-5';
  if (skill.includes('CSS')) return <LayoutIcon className={className} data-id="element-1308" />;
  if (skill.includes('JavaScript')) return <FileCodeIcon className={className} data-id="element-1309" />;
  if (skill.includes('HTML')) return <CodeIcon className={className} data-id="element-1310" />;
  if (skill.includes('Responsive')) return <GlobeIcon className={className} data-id="element-1311" />;
  return <BookOpenIcon className={className} data-id="element-1312" />;
}
function priorityConfig(priority: Priority) {
  switch (priority) {
    case 'critical':
      return {
        border: 'border-l-[#DB3725]',
        bg: 'bg-[#DB3725]/5',
        badge: 'bg-[#DB3725] text-white',
        badgeLabel: 'Critical',
        iconColor: 'text-[#DB3725]',
        icon: <AlertTriangle size={16} className="text-[#DB3725]" data-id="element-1313" />
      };
    case 'warning':
      return {
        border: 'border-l-[#F59E0B]',
        bg: 'bg-[#F59E0B]/5',
        badge: 'bg-[#F59E0B] text-white',
        badgeLabel: 'Warning',
        iconColor: 'text-[#F59E0B]',
        icon: <TrendingUp size={16} className="text-[#F59E0B]" data-id="element-1314" />
      };
    case 'positive':
      return {
        border: 'border-l-[#007558]',
        bg: 'bg-[#007558]/5',
        badge: 'bg-[#007558] text-white',
        badgeLabel: 'Positive',
        iconColor: 'text-[#007558]',
        icon: <Star size={16} className="text-[#007558]" data-id="element-1315" />
      };
  }
}
function SkillInsightCard({
  insight,
  isExpanded,
  onToggle,
  onActionClick
}: {
  insight: SkillInsight;
  isExpanded: boolean;
  onToggle: () => void;
  onActionClick?: (action: string, insight: SkillInsight) => void;
}) {
  const config = priorityConfig(insight.priority);
  return <motion.div layout className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${config.border} overflow-hidden`} data-id="element-1316">
      {/* Header — always visible */}
      <button onClick={onToggle} className="w-full text-left p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#30718D] focus-visible:ring-inset" data-id="element-1317">
        <div className="flex items-start justify-between gap-3" data-id="element-1318">
          <div className="flex-1 min-w-0" data-id="element-1319">
            {/* Skill name + priority badge */}
            <div className="flex items-center gap-2.5 mb-2" data-id="element-1320">
              <div className={`p-1.5 rounded-md ${config.bg}`} data-id="element-1321">
                <SkillIcon skill={insight.skill} data-id="element-1322" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1323">
                {insight.skill}
              </span>
              {insight.learnerName && <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-[#30718D]/10 text-[#30718D]" data-id="element-1324">
                  {insight.learnerName}
                </span>}
            </div>
            {/* Headline */}
            <p className="text-[15px] font-semibold text-gray-900 leading-snug" data-id="element-1325">
              {insight.headline}
            </p>
            {/* Meta row */}
            <div className="flex items-center gap-4 mt-2.5 text-xs text-gray-500" data-id="element-1326">
              <span className="flex items-center gap-1" data-id="element-1327">
                <Users size={12} data-id="element-1328" />
                {insight.affectedLearners} learners affected
              </span>
              <span className="flex items-center gap-1" data-id="element-1329">
                <Clock size={12} data-id="element-1330" />
                {insight.timestamp}
              </span>
            </div>
          </div>
          <div className="flex-shrink-0 mt-1 text-gray-400" data-id="element-1331">
            {isExpanded ? <ChevronUp size={18} data-id="element-1332" /> : <ChevronDown size={18} data-id="element-1333" />}
          </div>
        </div>
      </button>

      {/* Expandable details */}
      <AnimatePresence initial={false} data-id="element-1334">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1335">
            <div className="px-5 pb-5 pt-0" data-id="element-1336">
              <div className="border-t border-gray-100 pt-4" data-id="element-1337">
                {/* Metrics */}
                <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-1338">
                  {insight.metrics.map((metric, i) => <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-1339">
                      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1" data-id="element-1340">
                        {metric.label}
                      </div>
                      <div className="text-sm font-bold text-gray-900" data-id="element-1341">
                        {metric.value}
                      </div>
                    </div>)}
                </div>

                {/* Struggle areas */}
                {insight.struggleAreas && insight.struggleAreas.length > 0 && <div className="mb-4" data-id="element-1342">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-1343">
                      Problem Areas
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1344">
                      {insight.struggleAreas.map((area, i) => <span key={i} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${insight.priority === 'critical' ? 'bg-[#DB3725]/5 text-[#DB3725] border-[#DB3725]/20' : insight.priority === 'warning' ? 'bg-[#F59E0B]/5 text-[#92600a] border-[#F59E0B]/20' : 'bg-[#007558]/5 text-[#007558] border-[#007558]/20'}`} data-id="element-1345">
                          {area}
                        </span>)}
                    </div>
                  </div>}

                {/* Suggested actions */}
                <div data-id="element-1346">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-1347">
                    Suggested Actions
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-1348">
                    {insight.suggestedActions.map((action, i) => <button key={i} onClick={e => {
                  e.stopPropagation();
                  onActionClick?.(action, insight);
                }} className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-xs font-medium border border-gray-200 hover:border-[#30718D] hover:text-[#30718D] hover:bg-[#30718D]/5 transition-colors flex items-center gap-1.5" data-id="element-1349">
                        {action}
                        <ArrowRight size={10} className="opacity-50" data-id="element-1350" />
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
function formatAIMessage(content: string) {
  // Simple markdown-like formatting for AI responses
  const lines = content.split('\n');
  return lines.map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={i} className="font-bold text-gray-900 mt-2 mb-1" data-id="element-1351">
          {line.replace(/\*\*/g, '')}
        </p>;
    }
    if (line.includes('**')) {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return <p key={i} className="text-sm text-gray-700 leading-relaxed" data-id="element-1352">
          {parts.map((part, j) => part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold text-gray-900" data-id="element-1353">
                {part.replace(/\*\*/g, '')}
              </strong> : <span key={j} data-id="element-1354">{part}</span>)}
        </p>;
    }
    if (line.startsWith('•')) {
      return <p key={i} className="text-sm text-gray-700 leading-relaxed pl-2 flex gap-1.5" data-id="element-1355">
          <span className="text-[#30718D] flex-shrink-0" data-id="element-1356">•</span>
          <span data-id="element-1357">{line.substring(2)}</span>
        </p>;
    }
    if (line.trim() === '') {
      return <div key={i} className="h-2" data-id="element-1358" />;
    }
    return <p key={i} className="text-sm text-gray-700 leading-relaxed" data-id="element-1359">
        {line}
      </p>;
  });
}
function AIAssistantPanel({
  messages,
  isTyping,
  inputValue,
  onInputChange,
  onSend,
  onSuggestedQuestion
}: {
  messages: ChatMessage[];
  isTyping: boolean;
  inputValue: string;
  onInputChange: (val: string) => void;
  onSend: () => void;
  onSuggestedQuestion: (q: string) => void;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  const isEmpty = messages.length === 0 && !isTyping;
  return <div className="font-['Roboto'] bg-white shadow-[0px_3px_3px_-2px_rgba(0,24,35,0.05)] flex flex-col h-screen overflow-hidden lg:sticky lg:top-0" data-id="element-1360">
      {/* Header */}
      <header className="flex items-center justify-between h-12 px-4 border-b border-gray-100 flex-shrink-0" data-id="element-1361">
        <div className="flex items-center gap-3" data-id="element-1362">
          <MessageSquare size={18} className="text-[#DB3725]" fill="#DB3725" stroke="#DB3725" data-id="element-1363" />
          <h3 className="text-sm font-medium text-[#001823]" data-id="element-1364">Chat</h3>
        </div>
        <div className="flex items-center gap-1" data-id="element-1365">
          <button className="w-8 h-8 flex items-center justify-center rounded-full text-[#001823] hover:bg-gray-100 transition-colors" title="Expand / collapse" aria-label="Expand or collapse chat" data-id="element-1366">
            <ChevronsLeftRight size={18} data-id="element-1367" />
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full text-[#001823] hover:bg-gray-100 transition-colors" title="More options" aria-label="More options" data-id="element-1368">
            <MoreVertical size={18} data-id="element-1369" />
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full text-[#001823] hover:bg-gray-100 transition-colors" title="Minimize" aria-label="Minimize chat" data-id="element-1370">
            <ChevronDown size={18} data-id="element-1371" />
          </button>
        </div>
      </header>

      {/* Messages / Empty state */}
      <div className="flex-1 overflow-y-auto px-4" style={isEmpty ? {
      background: 'linear-gradient(90deg, #E3F1F7 0%, #FFFFFF 50%, #E3F1F7 100%)'
    } : undefined} data-id="element-1372">
        {isEmpty ? <div className="h-full flex flex-col items-center justify-center text-center px-2" data-id="element-1373">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-[#D9EEF2] flex items-center justify-center overflow-hidden" data-id="element-1374">
              <img src="https://cdn.magicpatterns.com/uploads/wx2adF4ofC4ZyYeARfvKrn/image.png" alt="Phoebe, your AI assistant" className="w-full h-full object-cover" data-id="element-1375" />
            </div>
            <p className="mt-3 text-sm font-bold text-gray-600" data-id="element-1376">
              Phoebe - AI assistant
            </p>
            <h2 className="mt-2 text-2xl font-bold text-[#001823]" data-id="element-1377">
              Hi there, how can I help?
            </h2>

            {/* Input pill */}
            <div className="w-full mt-6" data-id="element-1378">
              <ChatInput inputValue={inputValue} onInputChange={onInputChange} onKeyDown={handleKeyDown} onSend={onSend} isTyping={isTyping} data-id="element-1379" />
            </div>

            {/* Suggested chips */}
            <div className="mt-5 flex flex-wrap justify-center gap-2.5" data-id="element-1380">
              {suggestedQuestions.slice(0, 4).map((q, i) => <motion.button key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.06
          }} onClick={() => onSuggestedQuestion(q)} className="px-4 py-2 rounded-full border border-gray-300 text-sm text-[#001823] hover:bg-gray-50 hover:border-gray-400 transition-colors" data-id="element-1381">
                  {q}
                </motion.button>)}
            </div>
          </div> : <div className="py-4 space-y-4" data-id="element-1382">
            {messages.map(msg => <div key={msg.id} className="space-y-1" data-id="element-1383">
                {msg.role === 'assistant' && <div className="flex items-center gap-2 mb-1" data-id="element-1384">
                    <div className="w-6 h-6 rounded-full bg-[#D9EEF2] flex items-center justify-center overflow-hidden" data-id="element-1385">
                      <img src="https://cdn.magicpatterns.com/uploads/wx2adF4ofC4ZyYeARfvKrn/image.png" alt="Phoebe" className="w-full h-full object-cover" data-id="element-1386" />
                    </div>
                    <span className="text-xs font-bold text-gray-600" data-id="element-1387">
                      Phoebe - AI assistant
                    </span>
                  </div>}
                <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2
          }} className={`${msg.role === 'user' ? 'ml-auto max-w-[85%]' : 'max-w-[90%]'}`} data-id="element-1388">
                  <div className={`rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-[#F1F3F4] text-[#001823]' : 'bg-white border border-gray-200'}`} data-id="element-1389">
                    {msg.role === 'user' ? <p className="text-sm text-[#001823]" data-id="element-1390">{msg.content}</p> : <div className="space-y-1" data-id="element-1391">
                        {formatAIMessage(msg.content)}
                      </div>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 px-1" data-id="element-1392">
                    <span className="text-xs text-gray-500" data-id="element-1393">11:46 am</span>
                    {msg.role === 'assistant' && <div className="flex items-center gap-2" data-id="element-1394">
                        <button className="p-1 hover:bg-gray-100 rounded transition-colors" title="Helpful" data-id="element-1395">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-gray-600" data-id="element-1396">
                            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" data-id="element-1397" />
                          </svg>
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded transition-colors" title="Not helpful" data-id="element-1398">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-gray-600" data-id="element-1399">
                            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" data-id="element-1400" />
                          </svg>
                        </button>
                      </div>}
                  </div>
                </motion.div>
                {msg.role === 'user' && <div className="text-right" data-id="element-1401">
                    <span className="text-xs font-medium text-gray-600" data-id="element-1402">
                      Tanya
                    </span>
                  </div>}
              </div>)}

            {isTyping && <div className="space-y-1" data-id="element-1403">
                <div className="flex items-center gap-2 mb-1" data-id="element-1404">
                  <div className="w-6 h-6 rounded-full bg-[#D9EEF2] flex items-center justify-center overflow-hidden" data-id="element-1405">
                    <img src="https://cdn.magicpatterns.com/uploads/wx2adF4ofC4ZyYeARfvKrn/image.png" alt="Phoebe" className="w-full h-full object-cover" data-id="element-1406" />
                  </div>
                  <span className="text-xs font-bold text-gray-600" data-id="element-1407">
                    Phoebe - AI assistant
                  </span>
                </div>
                <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="max-w-[90%]" data-id="element-1408">
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3" data-id="element-1409">
                    <TypingIndicator data-id="element-1410" />
                  </div>
                </motion.div>
              </div>}

            <div ref={messagesEndRef} data-id="element-1411" />

            {/* Suggested chips after conversation */}
            {messages.length > 0 && !isTyping && <div className="pt-2 flex flex-wrap justify-center gap-2.5" data-id="element-1412">
                {suggestedQuestions.slice(0, 4).map((q, i) => <button key={i} onClick={() => onSuggestedQuestion(q)} className="px-4 py-2 rounded-full border border-gray-300 text-sm text-[#001823] hover:bg-gray-50 hover:border-gray-400 transition-colors" data-id="element-1413">
                    {q}
                  </button>)}
              </div>}
          </div>}
      </div>

      {/* Input (conversation mode) */}
      {!isEmpty && <div className="px-4 pt-3 flex-shrink-0" data-id="element-1414">
          <ChatInput inputValue={inputValue} onInputChange={onInputChange} onKeyDown={handleKeyDown} onSend={onSend} isTyping={isTyping} data-id="element-1415" />
        </div>}

      {/* Footer disclaimer */}
      <footer className="px-4 py-3 mt-3 border-t border-gray-100 flex-shrink-0" data-id="element-1416">
        <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-1417">
          Chats may be monitored, stored, and used as described in our Privacy
          Policy. By continuing, you agree to our{' '}
          <a href="#" className="text-[#30718D] underline inline-flex items-center gap-0.5 hover:text-[#265a70]" data-id="element-1418">
            Terms &amp; Conditions
            <ExternalLink size={11} data-id="element-1419" />
          </a>{' '}
          and acknowledge our{' '}
          <a href="#" className="text-[#30718D] underline inline-flex items-center gap-0.5 hover:text-[#265a70]" data-id="element-1420">
            Privacy Policy
            <ExternalLink size={11} data-id="element-1421" />
          </a>
          .
        </p>
      </footer>
    </div>;
}
function ChatInput({
  inputValue,
  onInputChange,
  onKeyDown,
  onSend,
  isTyping
}: {
  inputValue: string;
  onInputChange: (val: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  isTyping: boolean;
}) {
  return <div className="relative" data-id="element-1422">
      <input type="text" value={inputValue} onChange={e => onInputChange(e.target.value)} onKeyDown={onKeyDown} placeholder="Ask anything" maxLength={475} className="w-full pl-5 pr-12 py-3 text-sm text-[#001823] placeholder:text-gray-500 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DB3725] focus:border-transparent transition-shadow" data-id="element-1423" />
      <button onClick={onSend} disabled={!inputValue.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#DB3725] disabled:opacity-40 disabled:hover:text-gray-400 disabled:cursor-not-allowed transition-colors" aria-label="Send message" data-id="element-1424">
        <Send size={18} data-id="element-1425" />
      </button>
    </div>;
}
// ─── Main Page ───────────────────────────────────────────────────────────────
export function AlertsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Left panel state
  const [priorityFilter, setPriorityFilter] = useState<'all' | Priority>('all');
  const [skillFilter, setSkillFilter] = useState('All Skills');
  const [learnerFilter, setLearnerFilter] = useState('All Learners');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  // Right panel state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [nextId, setNextId] = useState(1);
  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Filter insights based on all filters
  const filteredInsights = skillInsights.filter(insight => {
    const matchesPriority = priorityFilter === 'all' || insight.priority === priorityFilter;
    const matchesSkill = skillFilter === 'All Skills' || insight.skill === skillFilter || insight.skill === 'All Skills' || insight.skill === 'Multiple Skills';
    // Learner filter logic
    let matchesLearner = true;
    if (learnerFilter === 'All Learners') {
      // Show only system-wide/skill-level insights (no learnerName)
      matchesLearner = !insight.learnerName;
    } else {
      // Show only insights for the selected learner
      matchesLearner = insight.learnerName === learnerFilter;
    }
    return matchesPriority && matchesSkill && matchesLearner;
  });
  // Counts based on current learner filter scope
  const scopedInsights = skillInsights.filter(i => {
    if (learnerFilter === 'All Learners') return !i.learnerName;
    return i.learnerName === learnerFilter;
  });
  const criticalCount = scopedInsights.filter(i => i.priority === 'critical').length;
  const warningCount = scopedInsights.filter(i => i.priority === 'warning').length;
  const positiveCount = scopedInsights.filter(i => i.priority === 'positive').length;
  const uniqueSkills = new Set(scopedInsights.map(i => i.skill));
  // Track which insights the AI has already proactively contextualized
  // so we don't re-post the same context every time a card is re-opened.
  const contextualizedRef = useRef<Set<number>>(new Set());
  // Build a contextual, behavior-aware briefing for a specific insight.
  const buildInsightContext = (insight: SkillInsight): string => {
    const subject = insight.learnerName ? insight.learnerName : `${insight.affectedLearners} learner${insight.affectedLearners !== 1 ? 's' : ''}`;
    const tone = insight.priority === 'critical' ? "This one's urgent — here's what stands out." : insight.priority === 'warning' ? "Worth keeping an eye on — here's the picture." : "Good news worth celebrating — here's the picture.";
    let text = `**${insight.skill}${insight.learnerName ? ` — ${insight.learnerName}` : ''}**\n\n${insight.headline}.\n\n${tone}\n\n`;
    text += `**The numbers:**\n`;
    insight.metrics.forEach(m => {
      text += `• ${m.label}: ${m.value}\n`;
    });
    if (insight.struggleAreas && insight.struggleAreas.length > 0) {
      text += `\n**What the data shows:**\n`;
      insight.struggleAreas.forEach(a => {
        text += `• ${a}\n`;
      });
    }
    text += `\n**Who's affected:** ${subject}\n`;
    text += insight.priority === 'positive' ? `\nWant help recognizing this and sharing it with the team?` : `\nWant me to walk you through how to support them? Pick a suggested action on the card, or ask me anything below.`;
    return text;
  };
  // Proactively surface context in the chat when an alert is opened.
  const provideInsightContext = (insight: SkillInsight) => {
    if (contextualizedRef.current.has(insight.id)) return;
    contextualizedRef.current.add(insight.id);
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        // Namespaced id so context messages never collide with the chat sequence
        id: 1000000 + insight.id,
        role: 'assistant',
        content: buildInsightContext(insight)
      }]);
      setIsTyping(false);
    }, 900);
  };
  const toggleCard = (insight: SkillInsight) => {
    const isOpening = !expandedCards.has(insight.id);
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(insight.id)) {
        next.delete(insight.id);
      } else {
        next.add(insight.id);
      }
      return next;
    });
    // On open (interaction), give the AI contextual awareness of this alert.
    if (isOpening) {
      provideInsightContext(insight);
    }
  };
  // Chat functions
  const sendMessage = (content: string) => {
    if (!content.trim() || isTyping) return;
    const userMsg: ChatMessage = {
      id: nextId,
      role: 'user',
      content: content.trim()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    setNextId(prev => prev + 2);
    // Find best matching mock response
    const lowerContent = content.toLowerCase();
    let responseText = "I've analyzed your data and here are my findings:\n\nBased on the current skill performance metrics, I can see several areas that need attention. The most pressing issues are in CSS Fundamentals and JavaScript Basics, where learners are consistently struggling.\n\n**Key takeaway:** Focus on restructuring content for the skills with the highest failure rates, and consider implementing the video tutorial strategy that worked well for HTML Basics.";
    for (const [question, response] of Object.entries(mockResponses)) {
      if (lowerContent.includes(question.toLowerCase().slice(0, 20)) || question.toLowerCase().includes(lowerContent.slice(0, 20))) {
        responseText = response;
        break;
      }
    }
    // Simulate typing delay
    setTimeout(() => {
      const aiMsg: ChatMessage = {
        id: nextId + 1,
        role: 'assistant',
        content: responseText
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };
  const handleSend = () => sendMessage(inputValue);
  const handleSuggestedQuestion = (q: string) => sendMessage(q);
  const handleActionClick = (action: string, insight: SkillInsight) => {
    // Generate a detailed AI response based on the action and insight
    const actionPrompt = `Tell me more about: "${action}" for ${insight.skill}`;
    const userMsg: ChatMessage = {
      id: nextId,
      role: 'user',
      content: actionPrompt
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);
    setNextId(prev => prev + 2);
    // Generate detailed response based on the action - focused on learner support
    let responseText = '';
    if (action.toLowerCase().includes('study session') || action.toLowerCase().includes('study group')) {
      responseText = `**Organizing Study Sessions for ${insight.skill}**\n\n**Purpose:**\nHelp learners collaborate, share knowledge, and support each other through challenging concepts.\n\n**Setup Steps:**\n1. **Identify participants** - Reach out to the ${insight.affectedLearners} affected learners\n2. **Schedule sessions** - Find times that work for most learners (consider time zones)\n3. **Set clear objectives** - Focus on specific struggle areas: ${insight.struggleAreas?.join(', ') || 'key concepts'}\n4. **Facilitate effectively** - Create a supportive environment where questions are encouraged\n\n**Best Practices:**\n• Keep groups small (4-6 learners) for better engagement\n• Mix skill levels to enable peer teaching\n• Provide discussion prompts and practice problems\n• Record sessions for learners who can't attend live\n\n**Expected Outcomes:**\n• Improved understanding through peer explanation\n• Increased confidence and motivation\n• Stronger sense of learning community\n• Better retention of concepts\n\n**Follow-up:** Check in with participants after 1 week to assess progress and gather feedback.`;
    } else if (action.toLowerCase().includes('mentor') || action.toLowerCase().includes('peer')) {
      responseText = `**Setting Up Peer Mentoring for ${insight.skill}**\n\n**Program Overview:**\nConnect struggling learners with successful peers who can provide guidance, encouragement, and practical tips.\n\n**Mentor Selection:**\n• Identify learners who scored 85%+ on ${insight.skill}\n• Look for those who enjoy helping others\n• Ensure they have time to commit (2-3 hours/week)\n• Provide mentor training and guidelines\n\n**Mentee Matching:**\n• Consider learning styles and schedules\n• Match based on specific struggle areas\n• Set clear expectations for both parties\n• Establish communication preferences\n\n**Structure:**\n• Weekly 30-minute check-ins\n• Focus on specific challenges\n• Share study strategies and resources\n• Celebrate small wins together\n\n**Support for Mentors:**\n• Provide conversation starters and tips\n• Create a mentor community for peer support\n• Recognize their contributions publicly\n• Offer feedback and guidance as needed\n\n**Success Metrics:**\n• Improved scores for mentees\n• Increased engagement and confidence\n• Reduced time to skill completion\n• High satisfaction from both mentors and mentees`;
    } else if (action.toLowerCase().includes('check-in') || action.toLowerCase().includes('meeting') || action.toLowerCase().includes('1-on-1')) {
      responseText = `**Conducting Effective 1-on-1 Check-ins**\n\n**Meeting Purpose:**\nUnderstand learner challenges, provide personalized support, and create an action plan for success.\n\n**Preparation:**\n• Review learner's performance data and attempt history\n• Note specific struggle areas: ${insight.struggleAreas?.join(', ') || 'areas needing attention'}\n• Prepare open-ended questions\n• Block 30-45 minutes for the conversation\n\n**Conversation Structure:**\n\n**1. Build Rapport (5 min)**\n• Start with a warm greeting\n• Ask how they're feeling about their learning journey\n• Create a safe, judgment-free space\n\n**2. Understand Challenges (10 min)**\n• "What concepts feel most confusing?"\n• "What's been your study approach so far?"\n• "Are there any barriers preventing your progress?"\n• "What learning methods work best for you?"\n\n**3. Identify Solutions (10 min)**\n• Discuss available resources and support\n• Explore different learning strategies\n• Address any misconceptions\n• Brainstorm ways to overcome barriers\n\n**4. Create Action Plan (10 min)**\n• Set specific, achievable goals\n• Schedule practice time\n• Identify support resources\n• Plan follow-up check-in\n\n**5. Encouragement (5 min)**\n• Acknowledge their effort and progress\n• Express confidence in their ability\n• Remind them of available support\n\n**Follow-up Actions:**\n• Send summary email with action items\n• Share recommended resources\n• Schedule next check-in (1-2 weeks)\n• Monitor progress and provide encouragement`;
    } else if (action.toLowerCase().includes('encouragement') || action.toLowerCase().includes('congratulations') || action.toLowerCase().includes('celebrate')) {
      responseText = `**Recognizing and Celebrating Learner Success**\n\n**Why Recognition Matters:**\nPositive reinforcement boosts motivation, builds confidence, and encourages continued effort.\n\n**Recognition Strategies:**\n\n**For Individual Achievements:**\n• Send personalized congratulations message\n• Highlight specific accomplishments\n• Share their progress metrics\n• Acknowledge the effort behind the success\n\n**Sample Message:**\n"Congratulations on your amazing progress in ${insight.skill}! Your dedication really shows - improving your score and demonstrating strong mastery. Your persistence and hard work are inspiring. Keep up the excellent work!"\n\n**For Group Successes:**\n• Announce achievements in team communications\n• Create a success story highlight\n• Share anonymized metrics showing collective progress\n• Thank learners for their commitment\n\n**Additional Recognition Ideas:**\n• Feature in newsletter or bulletin\n• Invite to mentor program\n• Offer advanced learning opportunities\n• Provide digital badges or certificates\n• Share success tips with other learners\n\n**Best Practices:**\n• Be specific about what they did well\n• Recognize effort, not just outcomes\n• Make it timely (within 24-48 hours)\n• Be genuine and authentic\n• Encourage them to share their strategies\n\n**Impact:**\n• Increased motivation and engagement\n• Stronger sense of accomplishment\n• Inspiration for other learners\n• Positive learning community culture`;
    } else if (action.toLowerCase().includes('resources') || action.toLowerCase().includes('share')) {
      responseText = `**Sharing Helpful Resources for ${insight.skill}**\n\n**Resource Curation Strategy:**\nProvide targeted, high-quality resources that address specific learning gaps.\n\n**Types of Resources to Share:**\n\n**1. Practice Materials**\n• Interactive exercises and coding challenges\n• Practice problems with solutions\n• Hands-on projects and examples\n• Self-assessment quizzes\n\n**2. Learning Aids**\n• Visual diagrams and infographics\n• Cheat sheets and quick reference guides\n• Video tutorials and demonstrations\n• Step-by-step walkthroughs\n\n**3. Community Resources**\n• Study group schedules\n• Peer mentor contact information\n• Office hours calendar\n• Discussion forum links\n\n**For ${insight.skill} Specifically:**\n${insight.struggleAreas ? `Focus on resources covering:\n• ${insight.struggleAreas.join('\n• ')}` : 'Focus on core concepts and common challenges'}\n\n**Delivery Best Practices:**\n• Organize resources by difficulty level\n• Include brief descriptions of each resource\n• Highlight which resources address specific struggles\n• Provide estimated time commitment\n• Make resources easily accessible\n\n**Follow-up:**\n• Ask learners which resources they found most helpful\n• Check in after 3-5 days to see if they need additional support\n• Adjust resource recommendations based on feedback\n• Track which resources lead to improved performance\n\n**Expected Impact:**\n• Learners feel supported and equipped\n• Reduced frustration and confusion\n• Improved self-directed learning\n• Better skill mastery outcomes`;
    } else if (action.toLowerCase().includes('reminder') || action.toLowerCase().includes('enrollment')) {
      responseText = `**Encouraging Skill Enrollment and Engagement**\n\n**Situation:**\nLow enrollment in ${insight.skill} despite it being important for professional development.\n\n**Communication Strategy:**\n\n**1. Highlight Benefits (Not Requirements)**\n• Focus on career advantages\n• Share success stories from past learners\n• Explain real-world applications\n• Connect to learner goals\n\n**Sample Message:**\n"${insight.skill} is a valuable skill that can enhance your professional capabilities. Many learners have found it helps them [specific benefit]. The skill is designed to be approachable and practical. We're here to support you every step of the way!"\n\n**2. Address Concerns**\n• Acknowledge if the skill seems challenging\n• Explain available support resources\n• Share that many learners succeed with practice\n• Offer to answer questions\n\n**3. Make It Easy**\n• Provide clear enrollment steps\n• Offer orientation or preview session\n• Share what to expect\n• Highlight flexible pacing options\n\n**4. Create Urgency (Gently)**\n• Mention upcoming cohort start dates\n• Note limited mentor availability\n• Highlight career relevance\n• Remind of professional development goals\n\n**Timing:**\n• Send initial reminder with benefits focus\n• Follow up after 3-5 days with success stories\n• Offer personal support call for those still hesitant\n• Final reminder with enrollment deadline\n\n**Support Offers:**\n• "Need help getting started? I'm here to help!"\n• "Questions about the skill? Let's schedule a quick call"\n• "Want to connect with others taking this skill?"\n\n**Expected Outcomes:**\n• Increased enrollment rates\n• Reduced anxiety about skill difficulty\n• Stronger learner engagement from the start\n• Better completion rates`;
    } else if (action.toLowerCase().includes('office hours') || action.toLowerCase().includes('support hours') || action.toLowerCase().includes('q&a')) {
      responseText = `**Setting Up Effective Support Hours**\n\n**Purpose:**\nProvide dedicated time for learners to get help, ask questions, and receive guidance on ${insight.skill}.\n\n**Planning:**\n\n**1. Schedule Strategically**\n• Offer multiple time slots (morning, afternoon, evening)\n• Include weekend hours if failure rates are higher then\n• Consider time zones for remote learners\n• Aim for 2-3 sessions per week\n\n**2. Promote Widely**\n• Send calendar invites to affected learners\n• Post schedule in learning platform\n• Send reminders 24 hours before\n• Make it easy to join (one-click links)\n\n**3. Structure Sessions**\n• Start with general Q&A (15 min)\n• Address common struggle areas: ${insight.struggleAreas?.join(', ') || 'key concepts'}\n• Work through practice problems together\n• Leave time for individual questions\n\n**Session Best Practices:**\n• Create welcoming, judgment-free environment\n• Encourage all questions ("no question is too basic")\n• Use screen sharing for demonstrations\n• Record sessions for those who can't attend\n• Follow up with summary and resources\n\n**For ${insight.skill} Specifically:**\n• Focus on areas where learners struggle most\n• Provide hands-on practice opportunities\n• Share tips and strategies from successful learners\n• Address common misconceptions\n\n**Engagement Tips:**\n• Use polls to identify topics to cover\n• Encourage peer-to-peer help\n• Celebrate "aha moments"\n• Create safe space for mistakes\n\n**Follow-up:**\n• Send session recording and notes\n• Share additional resources discussed\n• Gather feedback on helpfulness\n• Adjust format based on learner needs\n\n**Expected Impact:**\n• Reduced learner frustration\n• Faster problem resolution\n• Stronger sense of support\n• Improved skill performance`;
    } else if (action.toLowerCase().includes('time management') || action.toLowerCase().includes('scheduling')) {
      responseText = `**Supporting Learners with Time Management**\n\n**Situation:**\nLearners taking too long between attempts or showing inconsistent engagement patterns.\n\n**Support Approach:**\n\n**1. Understand the Barriers**\n• Schedule a brief check-in call\n• Ask about their current schedule and commitments\n• Identify specific time management challenges\n• Listen without judgment\n\n**Key Questions:**\n• "What's your typical weekly schedule like?"\n• "When do you usually have time for learning?"\n• "What gets in the way of consistent practice?"\n• "What would make it easier to stay on track?"\n\n**2. Provide Practical Strategies**\n\n**Time Blocking:**\n• Help them identify 2-3 consistent study times per week\n• Suggest starting with small blocks (20-30 minutes)\n• Recommend calendar blocking for accountability\n\n**Progress Tracking:**\n• Set weekly micro-goals\n• Celebrate small wins\n• Use progress check-ins as motivation\n• Track improvement over time\n\n**Accountability:**\n• Pair with study buddy\n• Schedule regular check-ins\n• Send gentle progress reminders\n• Celebrate consistency\n\n**3. Flexible Support Options**\n• Offer asynchronous resources for busy schedules\n• Provide bite-sized learning modules\n• Allow self-paced progress\n• Extend deadlines when appropriate\n\n**4. Address Common Challenges**\n\n**"I'm too busy":**\n• Help prioritize learning in their schedule\n• Show how small, consistent efforts add up\n• Suggest integrating learning into existing routines\n\n**"I keep procrastinating":**\n• Break skill into smaller milestones\n• Create accountability partnerships\n• Celebrate starting, not just finishing\n\n**"I lose momentum between attempts":**\n• Schedule regular touchpoints\n• Send encouraging reminders\n• Connect with active learners\n\n**Follow-up Plan:**\n• Weekly progress check-ins (brief, 5-10 min)\n• Adjust schedule as needed\n• Recognize consistent effort\n• Provide ongoing encouragement\n\n**Expected Outcomes:**\n• More consistent engagement\n• Reduced gaps between attempts\n• Improved completion rates\n• Better work-life-learning balance`;
    } else {
      responseText = `**Supporting Learners: ${action}**\n\n**Context:**\nThis action addresses ${insight.priority === 'critical' ? 'an urgent' : insight.priority === 'warning' ? 'an important' : 'a positive'} situation with ${insight.skill}, affecting ${insight.affectedLearners} learner${insight.affectedLearners !== 1 ? 's' : ''}.\n\n**Your Role as Admin:**\nAs a facilitator, your goal is to help learners succeed by providing support, encouragement, and resources - not by creating content.\n\n**Recommended Approach:**\n\n**1. Assess the Situation (Week 1)**\n• Review learner performance data\n• Identify specific barriers to success\n• Understand individual circumstances\n• Gather learner feedback\n\n**2. Provide Targeted Support (Weeks 2-3)**\n• Reach out to affected learners personally\n• Offer 1-on-1 check-ins to understand needs\n• Connect learners with peer mentors\n• Share relevant resources and tools\n• Create study groups or support sessions\n\n**3. Monitor Progress (Week 4)**\n• Track engagement and performance improvements\n• Adjust support strategies as needed\n• Celebrate small wins and progress\n• Maintain regular communication\n\n**4. Recognize Success (Ongoing)**\n• Acknowledge effort and improvement\n• Share success stories to inspire others\n• Thank learners for their dedication\n• Invite high performers to mentor roles\n\n**Support Resources You Can Provide:**\n• Additional practice materials\n• Study group schedules\n• Office hours and Q&A sessions\n• Peer mentor connections\n• Time management guidance\n• Encouragement and motivation\n\n**Communication Tips:**\n• Be empathetic and supportive\n• Focus on learner strengths\n• Offer help without judgment\n• Make support easily accessible\n• Follow up consistently\n\n**Expected Outcomes:**\n• Improved learner confidence\n• Better engagement and persistence\n• Higher success rates\n• Stronger learning community\n• Increased skill completion\n\n**Next Steps:**\nWould you like specific guidance on implementing this support strategy?`;
    }
    // Simulate typing delay
    setTimeout(() => {
      const aiMsg: ChatMessage = {
        id: nextId + 1,
        role: 'assistant',
        content: responseText
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };
  const skillOptions = ['All Skills', ...Array.from(uniqueSkills)];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1426">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1427">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-1428">
          <div className="flex items-center h-16" data-id="element-1429">
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1430">LOGO</div>

            <nav className="flex items-center gap-8 ml-12" data-id="element-1431">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1432">
                <Home size={18} data-id="element-1433" />
                <span data-id="element-1434">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1435">
                <BarChart3 size={18} data-id="element-1436" />
                <span data-id="element-1437">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1438">
                <FileText size={18} data-id="element-1439" />
                <span data-id="element-1440">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1441">
                <UserCog size={18} data-id="element-1442" />
                <span data-id="element-1443">Users</span>
              </Link>
            </nav>

            <div className="flex items-center gap-4 ml-auto" data-id="element-1444">
              <Link to="/alerts" className="relative p-2 text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-1445">
                <Bell size={20} data-id="element-1446" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1447" />
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1448">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1449">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1450">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-1451">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1452" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1453">
                    <div className="py-2" data-id="element-1454">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1455">
                        <Users size={18} className="text-gray-600" data-id="element-1456" />
                        <span className="font-medium text-gray-900" data-id="element-1457">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1458">
                        <Home size={18} className="text-gray-600" data-id="element-1459" />
                        <span className="font-medium text-gray-900" data-id="element-1460">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1461">
                        <UserCog size={18} className="text-gray-600" data-id="element-1462" />
                        <span className="font-medium text-gray-900" data-id="element-1463">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1464">
                        <SettingsIcon size={18} className="text-gray-600" data-id="element-1465" />
                        <span className="font-medium text-gray-900" data-id="element-1466">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1467" />
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1468">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1469">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1470" />
                          <polyline points="16 17 21 12 16 7" data-id="element-1471" />
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1472" />
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1473">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="max-w-[1440px] mx-auto px-8 py-8" data-id="element-1474">
        {/* Two-panel layout */}
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-1475">
          {/* ─── LEFT PANEL: Skill Insights ─── */}
          <div className="flex-1 lg:w-[62%] min-w-0" data-id="element-1476">
            {/* Header */}
            <div className="mb-6" data-id="element-1477">
              <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1478">
                Skill Insights & Actions
              </h1>
              <p className="text-gray-600 text-sm" data-id="element-1479">
                {learnerFilter === 'All Learners' ? 'AI-powered analysis of skill performance across your organization' : `Viewing insights for ${learnerFilter}`}
              </p>
            </div>

            {/* Summary stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-1480">
              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.05
            }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1481">
                <div className="flex items-center justify-between mb-2" data-id="element-1482">
                  <Target size={20} className="text-[#30718D]" data-id="element-1483" />
                  <span className="text-2xl font-bold text-gray-900" data-id="element-1484">
                    {learnerFilter === 'All Learners' ? uniqueSkills.size : scopedInsights.length}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600" data-id="element-1485">
                  {learnerFilter === 'All Learners' ? 'Skills Monitored' : 'Total Insights'}
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1486">
                <div className="flex items-center justify-between mb-2" data-id="element-1487">
                  <AlertTriangle size={20} className="text-[#DB3725]" data-id="element-1488" />
                  <span className="text-2xl font-bold text-[#DB3725]" data-id="element-1489">
                    {criticalCount}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600" data-id="element-1490">
                  Critical Issues
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.15
            }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1491">
                <div className="flex items-center justify-between mb-2" data-id="element-1492">
                  <TrendingUp size={20} className="text-[#F59E0B]" data-id="element-1493" />
                  <span className="text-2xl font-bold text-[#F59E0B]" data-id="element-1494">
                    {warningCount}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600" data-id="element-1495">
                  {learnerFilter === 'All Learners' ? 'Improvement Opportunities' : 'Needs Attention'}
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1496">
                <div className="flex items-center justify-between mb-2" data-id="element-1497">
                  <Star size={20} className="text-[#007558]" data-id="element-1498" />
                  <span className="text-2xl font-bold text-[#007558]" data-id="element-1499">
                    {positiveCount}
                  </span>
                </div>
                <div className="text-xs font-medium text-gray-600" data-id="element-1500">
                  {learnerFilter === 'All Learners' ? 'Success Stories' : 'Positive'}
                </div>
              </motion.div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-5 flex-wrap" data-id="element-1501">
              <div className="flex items-center gap-1.5 bg-white rounded-lg border border-gray-200 p-1" data-id="element-1502">
                <button onClick={() => setPriorityFilter('all')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${priorityFilter === 'all' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1503">
                  All ({scopedInsights.length})
                </button>
                <button onClick={() => setPriorityFilter('critical')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${priorityFilter === 'critical' ? 'bg-[#DB3725] text-white shadow-sm' : 'text-[#DB3725] hover:bg-[#DB3725]/10'}`} data-id="element-1504">
                  Critical ({criticalCount})
                </button>
                <button onClick={() => setPriorityFilter('warning')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${priorityFilter === 'warning' ? 'bg-[#F59E0B] text-white shadow-sm' : 'text-[#92600a] hover:bg-[#F59E0B]/10'}`} data-id="element-1505">
                  Warning ({warningCount})
                </button>
                <button onClick={() => setPriorityFilter('positive')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${priorityFilter === 'positive' ? 'bg-[#007558] text-white shadow-sm' : 'text-[#007558] hover:bg-[#007558]/10'}`} data-id="element-1506">
                  Positive ({positiveCount})
                </button>
              </div>

              <select value={skillFilter} onChange={e => setSkillFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1507">
                {skillOptions.map(skill => <option key={skill} value={skill} data-id="element-1508">
                    {skill}
                  </option>)}
              </select>

              <select value={learnerFilter} onChange={e => {
              setLearnerFilter(e.target.value);
              setPriorityFilter('all');
            }} className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1509">
                <option value="All Learners" data-id="element-1510">All Learners</option>
                {[...mockLearners].sort((a, b) => {
                const lastA = a.name.split(' ').slice(-1)[0].toLowerCase();
                const lastB = b.name.split(' ').slice(-1)[0].toLowerCase();
                return lastA.localeCompare(lastB);
              }).map(learner => <option key={learner.id} value={learner.name} data-id="element-1511">
                      {learner.name}
                    </option>)}
              </select>
            </div>

            {/* Insight cards */}
            <div className="space-y-3" data-id="element-1512">
              <AnimatePresence mode="popLayout" data-id="element-1513">
                {filteredInsights.map((insight, index) => <motion.div key={insight.id} initial={{
                opacity: 0,
                y: 16
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.97
              }} transition={{
                delay: index * 0.05
              }} layout data-id="element-1514">
                    <SkillInsightCard insight={insight} isExpanded={expandedCards.has(insight.id)} onToggle={() => toggleCard(insight)} onActionClick={handleActionClick} data-id="element-1515" />
                  </motion.div>)}
              </AnimatePresence>

              {filteredInsights.length === 0 && <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100" data-id="element-1516">
                  <Target size={40} className="mx-auto text-gray-300 mb-3" data-id="element-1517" />
                  <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-1518">
                    No insights match your filters
                  </h3>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-1519">
                    Try adjusting the priority or skill filter
                  </p>
                  <button onClick={() => {
                setPriorityFilter('all');
                setSkillFilter('All Skills');
              }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-1520">
                    <X size={14} data-id="element-1521" />
                    Clear filters
                  </button>
                </div>}
            </div>
          </div>

          {/* ─── RIGHT PANEL: AI Assistant ─── */}
          <div className="lg:w-[38%] flex-shrink-0" data-id="element-1522">
            <AIAssistantPanel messages={messages} isTyping={isTyping} inputValue={inputValue} onInputChange={setInputValue} onSend={handleSend} onSuggestedQuestion={handleSuggestedQuestion} data-id="element-1523" />
          </div>
        </div>
      </div>
    </div>;
}